/**
 * Home — FR-20, and the front half of FR-70.
 *
 * One number and one list. The number is what the archive exists to produce:
 * how much can be withdrawn tax-free today. The list is what needs a person.
 *
 * Ordering is the design. A withdrawal that needs explaining comes first
 * (FR-80 — it is the one thing here with a deadline). Setup gaps come next,
 * because nothing can be filed without the establishment date. Then the
 * balance, then the queue. The breakdowns FR-20 asks for sit behind one
 * disclosure at the bottom: this is not a spend-analytics app (PRD §3).
 */
import { Link } from 'react-router-dom';
import { claims } from '../auth.js';
import { useApi } from '../hooks.js';
import { isStale, latestBalance } from '../trends.js';
import { Balance, ErrorBox, Icon, Money, Notice, Skeleton, formatDate } from '../ui.js';
import { attention, byPriority, expenseTitle, sourceIcon } from '../labels.js';
import type { AppConfigResponse, DashboardResponse, Expense, HsaBalance } from '../types.js';

const SHOWN = 8;

export function Home() {
  const dash = useApi<DashboardResponse>('/api/dashboard');
  const drafts = useApi<{ items: Expense[] }>('/api/expenses?status=draft&limit=200');
  const config = useApi<AppConfigResponse>('/api/config');
  const isAdmin = (claims()?.['cognito:groups'] ?? []).includes('admin');

  if (dash.error) return <ErrorBox error={dash.error} />;
  if (dash.loading || !dash.data) return <Skeleton rows={4} />;

  const data = dash.data;
  const established = config.data?.config.hsa_establishment_date ?? null;
  const queue = [...(drafts.data?.items ?? [])].sort(byPriority(established));
  const needsSetup = !data.setup.establishment_date_set || data.setup.covered_persons === 0;
  const verified = data.counts.verified ?? 0;

  return (
    <>
      {data.alerts.map((alert, i) => (
        <Notice
          key={i}
          kind={alert.severity === 'critical' ? 'stop' : 'warn'}
          title={alert.title}
          actions={
            <Link className="btn" to="/withdrawals">
              Review withdrawals
            </Link>
          }
        >
          {alert.body}
        </Notice>
      ))}

      {needsSetup ? <Setup data={data} isAdmin={isAdmin} /> : null}

      <section className="hero" aria-label="Balance">
        <div className="label">Ready to claim</div>
        <Balance cents={data.claimable_balance_cents} />
        <div className="meta">
          {verified === 0
            ? 'Nothing filed yet.'
            : `${verified} expense${verified === 1 ? '' : 's'}` +
              (data.oldest_unreimbursed
                ? ` since ${formatDate(data.oldest_unreimbursed.date_of_service, { year: 'always' })}`
                : '')}
        </div>
      </section>

      <h2 className="section">
        <span>To review{queue.length > 0 ? ` · ${queue.length}` : ''}</span>
        {queue.length > SHOWN ? <Link to="/expenses?status=draft">See all</Link> : null}
      </h2>
      {drafts.loading ? (
        <Skeleton rows={2} />
      ) : queue.length === 0 ? (
        <div className="card">
          <p className="small muted">Nothing is waiting. Add a receipt when you have one.</p>
          <div className="actions" style={{ marginTop: 12 }}>
            <Link className="btn primary" to="/add">
              <Icon name="plus" />
              Add a receipt
            </Link>
          </div>
        </div>
      ) : (
        <div className="list">
          {queue.slice(0, SHOWN).map((e) => (
            <QueueRow key={e.expense_id} expense={e} established={established} />
          ))}
        </div>
      )}

      <Reminders data={data} isAdmin={isAdmin} balance={latestBalance(config.data?.config.hsa_balances)} />

      {verified > 0 ? (
        <details className="more">
          <summary>Breakdown by person, year and source</summary>
          <div className="stack">
            <Breakdown title="By person" rows={data.per_person} />
            <Breakdown title="By year" rows={data.per_year} />
            <Breakdown title="By source" rows={data.per_source} />
            <p className="xs muted">
              {data.total_records} records in the archive: {data.counts.draft ?? 0} to review,{' '}
              {verified} ready to claim, {data.counts.reimbursed ?? 0} reimbursed,{' '}
              {(data.counts.excluded ?? 0) + (data.counts.void ?? 0)} not claiming.
            </p>
          </div>
        </details>
      ) : null}
    </>
  );
}

function QueueRow({ expense, established }: { expense: Expense; established: string | null }) {
  const flag = attention(expense, established);
  return (
    <Link className="item" to={`/expenses/${expense.expense_id}`}>
      <span className="tile">
        <Icon name={sourceIcon(expense.source)} />
      </span>
      <span>
        <div className="title">{expenseTitle(expense)}</div>
        <div className="sub">
          {flag ? (
            <span className={`tone-${flag.tone}`}>
              <span className={`dot ${flag.tone}`} />
              {flag.label}
            </span>
          ) : (
            formatDate(expense.date_of_service)
          )}
          {/* Five receipts from one merchant are told apart by what was bought. */}
          {expense.service_description && expense.service_description !== expenseTitle(expense)
            ? ` · ${expense.service_description}`
            : ''}
        </div>
      </span>
      <span className="end">
        <Money cents={expense.amount_claimed_cents} />
      </span>
    </Link>
  );
}

/**
 * First-run setup, shown as a short to-do rather than as errors. The
 * establishment date really does block filing; saying so in red before the
 * person has done anything was the wrong first impression.
 */
function Setup({ data, isAdmin }: { data: DashboardResponse; isAdmin: boolean }) {
  const steps = [
    {
      done: data.setup.establishment_date_set,
      label: 'When was the HSA established?',
      note: 'Expenses from before that date cannot be claimed.',
      to: '/settings#hsa',
    },
    {
      done: data.setup.covered_persons > 0,
      label: 'Who is in the family?',
      note: 'You, your spouse, and your dependents.',
      to: '/settings#family',
    },
  ];
  return (
    <div className="card">
      <h3>Two things before the first receipt</h3>
      {!isAdmin ? (
        <p className="small muted">The account owner sets these up.</p>
      ) : null}
      <div className="steps">
        {steps.map((s) => (
          <div className={`step${s.done ? ' done' : ' current'}`} key={s.label}>
            <span className="marker" />
            <div>
              <h3>{s.label}</h3>
              {!s.done ? (
                <>
                  <p className="small muted">{s.note}</p>
                  {isAdmin ? (
                    <div className="actions" style={{ marginTop: 8 }}>
                      <Link className="btn primary" to={s.to}>
                        Set it
                      </Link>
                    </div>
                  ) : null}
                </>
              ) : null}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Things with a slow clock: the yearly declaration, an export in progress, an old HSA balance. */
function Reminders({ data, isAdmin, balance }: { data: DashboardResponse; isAdmin: boolean; balance: HsaBalance | null }) {
  const waiting = data.wizards.filter((w) => w.nudges_due > 0);
  const due = data.declarations?.due ?? [];
  // Only once there is a balance to be out of date; adding the first one is the Trends page's job.
  const oldBalance = balance !== null && isStale(balance, new Date());
  if (waiting.length === 0 && due.length === 0 && !oldBalance) return null;

  return (
    <>
      <h2 className="section">
        <span>Reminders</span>
      </h2>
      <div className="list">
        {due.length > 0 ? (
          <Link className="item" to="/settings#yearly">
            <span className="tile">
              <Icon name="flag" />
            </span>
            <span>
              <div className="title">
                Two questions for {due.length === 1 ? due[0] : `${due[0]}–${due[due.length - 1]}`}
              </div>
              <div className="sub">
                {isAdmin ? 'Once a year, in place of a checkbox on every receipt.' : 'For the account owner.'}
              </div>
            </span>
            <span className="end muted">
              <Icon name="chevron" />
            </span>
          </Link>
        ) : null}
        {oldBalance ? (
          <Link className="item" to="/trends#hsa">
            <span className="tile">
              <Icon name="bank" />
            </span>
            <span>
              <div className="title">Update the HSA balance</div>
              <div className="sub">
                {isAdmin
                  ? `Last copied in for ${formatDate(balance!.as_of, { year: 'always' })}. Copy it from the HSA’s website.`
                  : 'For the account owner.'}
              </div>
            </span>
            <span className="end muted">
              <Icon name="chevron" />
            </span>
          </Link>
        ) : null}
        {waiting.map((w) => (
          <Link className="item" to={`/wizards/${w.channel}`} key={w.channel}>
            <span className="tile">
              <Icon name="box" />
            </span>
            <span>
              <div className="title" style={{ textTransform: 'capitalize' }}>
                {w.channel} export is waiting
              </div>
              <div className="sub">
                {w.state === 'requested' ? 'Check for the confirmation email.' : 'The file has not been uploaded yet.'}
              </div>
            </span>
            <span className="end muted">
              <Icon name="chevron" />
            </span>
          </Link>
        ))}
      </div>
    </>
  );
}

function Breakdown({
  title,
  rows,
}: {
  title: string;
  rows: { key: string; count: number; amount_cents: number }[];
}) {
  if (rows.length === 0) return null;
  return (
    <div className="list">
      {rows.map((r, i) => (
        <div className="item plain" key={r.key}>
          <span>
            <div className="title" style={{ textTransform: 'capitalize' }}>
              {r.key.replace(/_/g, ' ')}
            </div>
            <div className="sub">
              {i === 0 ? `${title} · ` : ''}
              {r.count} expense{r.count === 1 ? '' : 's'}
            </div>
          </span>
          <span className="end">
            <Money cents={r.amount_cents} />
          </span>
        </div>
      ))}
    </div>
  );
}
